import axios from 'axios';

// Only first-party API requests advertise this client's protocol. This does not
// attest native capabilities or retrofit already-loaded older JavaScript.
axios.interceptors.request.use(config => {
  const origin = window.location.origin;
  const backend = (process.env.REACT_APP_BACKEND_URL || origin).replace(/\/+$/, '');
  const api = new URL(`${backend}/api/`, origin);
  const target = new URL(config.url, new URL(config.baseURL || origin, origin));
  if (target.origin === api.origin && (target.pathname === '/api' || target.pathname.startsWith('/api/'))) {
    config.headers.set('X-OpenRuns-Client-Protocol', '2');
  }
  return config;
});

const RECOVERY_KEY = 'openruns.clientProtocolRecoveryAttempted';
let recoveryHandled = false;

function recoverClient(error) {
  if (error.response?.status !== 426) return Promise.reject(error);
  let firstParty = false;
  try {
    const origin = window.location.origin;
    const backend = (process.env.REACT_APP_BACKEND_URL || origin).replace(/\/+$/, '');
    const api = new URL(`${backend}/api/`, origin);
    const config = error.config || error.response.config;
    const target = new URL(config.url, new URL(config.baseURL || origin, origin));
    firstParty = target.origin === api.origin &&
      (target.pathname === '/api' || target.pathname.startsWith('/api/'));
  } catch {
    // Unattributed responses cannot authorize navigation.
    console.warn('reason=client_protocol_recovery_unattributed');
  }
  if (!firstParty || recoveryHandled) return Promise.reject(error);

  // Synchronous latch shared by the default client and all created instances.
  // Never navigate unless the session marker can survive that navigation.
  recoveryHandled = true;
  try {
    const storage = window.sessionStorage;
    if (storage.getItem(RECOVERY_KEY) !== null) return Promise.reject(error);
    storage.setItem(RECOVERY_KEY, '1');
    if (storage.getItem(RECOVERY_KEY) !== '1') {
      console.warn('reason=client_protocol_recovery_storage_unavailable');
      return Promise.reject(error);
    }
  } catch {
    console.warn('reason=client_protocol_recovery_storage_unavailable');
    return Promise.reject(error);
  }
  try {
    const url = new URL(window.location.href);
    // A new document URL requests fresh HTML through the existing cache policy;
    // this does not guarantee bypassing every intermediary's cache rules.
    url.searchParams.append('__openruns_client_recovery', String(Date.now()));
    window.location.assign(url.href);
  } catch {
    console.warn('reason=client_protocol_recovery_navigation_failed');
  }
  // Preserve guidance and caller error handling; never replay the API request.
  return Promise.reject(error);
}

axios.interceptors.response?.use(response => response, recoverClient);
if (typeof axios.create === 'function') {
  const create = axios.create.bind(axios);
  axios.create = config => {
    const client = create(config);
    client.interceptors.response?.use(response => response, recoverClient);
    return client;
  };
}

export default axios;
